import { useState } from 'react';
import { useClientTranslation } from '@/lib/core/i18n';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { resolveMediaUrl } from '../ui/resolveMediaUrl';
import { resolveHeadingLevel } from '../ui/heading-level';
import { MediaSlider } from '../ui/media-slider';
import { MediaSliderCardBody } from '../ui/media-slider-card';
import { ShopGroupSheet } from './shop-group-sheet';
import type { ImageBlock, LinkBlock } from './types';

interface ShopSliderItem {
  image?: ImageBlock | null;
  title?: string | null;
  location?: string | null;
  link?: LinkBlock | null;
}

interface ShopSliderBlockProps {
  title?: string | null;
  titleHeading?: string | null;
  items?: ShopSliderItem[] | null;
}

export function ShopSliderBlock({ title, titleHeading, items }: ShopSliderBlockProps) {
  const t = useClientTranslation();
  const headingAs = resolveHeadingLevel(titleHeading);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  if (!items?.length) return null;

  return (
    <div className="flex flex-col gap-6 pl-4">
      <div className="flex items-center justify-between gap-4 pr-4">
        {title && (
          <Heading
            as={headingAs}
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[24px] leading-normal font-semibold"
          >
            {title}
          </Heading>
        )}
        <Button
          variant="tertiary-link"
          palette="dark"
          className="h-auto min-w-0 shrink-0 rounded-none px-0"
          onClick={() => setIsSheetOpen(true)}
        >
          {t('App.Labels.ViewAll')}
        </Button>
      </div>

      <MediaSlider
        items={items}
        gapClassName="gap-4"
        gapPx={16}
        fallbackRatio={0.63}
        renderItem={(item, i, cardProps) => {
          const imageUrl = resolveMediaUrl(item.image?.url);
          const Card = item.link?.url ? 'a' : 'div';
          return (
            <Card
              key={i}
              href={item.link?.url ?? undefined}
              target={item.link?.url ? (item.link?.target ?? '_self') : undefined}
              rel={item.link?.target === '_blank' ? 'noopener noreferrer' : undefined}
              className="flex w-[244px] shrink-0 snap-start flex-col gap-2"
              {...cardProps}
            >
              <MediaSliderCardBody
                imageUrl={imageUrl}
                imageAlt={item.title ?? ''}
                title={item.title}
                subtitle={item.location}
              />
            </Card>
          );
        }}
      />

      <ShopGroupSheet open={isSheetOpen} onOpenChange={setIsSheetOpen} />
    </div>
  );
}
